import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

import QuestionCard from '../components/questions/QuestionCard';
import ShortAnswerQuestionCard from '../components/questions/ShortAnswerQuestionCard';
import { TagDirectoryResponse, questionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import FreeTierLimitModal from '../components/modals/FreeTierLimitModal';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { isFreeTierLimitError } from '../utils/freeTier';
import {
  QuestionApiModel,
  QuestionTag,
  ShortAnswerQuestionGroup,
  isShortAnswerQuestionGroup,
} from '../types/questions';
import { buildPracticeQuestionSet } from '../utils/questions';
import { AttemptResult } from '../types/practice';
import { getUserMessage } from '../utils/errors';
import { useActiveSubjectId } from '../subjects/subjectContextValue';
import { SUBJECT_AREAS } from '../subjects/areas';

interface TagDirectoryEntry extends QuestionTag {
  question_count: number;
  area_of_study: string;
}

type TagDirectory = Record<string, TagDirectoryEntry[]>;


type QuestionTypeSelection = 'MC' | 'SA' | 'ER' | 'BOTH';
type PracticeQuestion = QuestionApiModel | ShortAnswerQuestionGroup;

const BOOKMARKED_TAG_ID = 'bookmarked';

const updatePracticeQuestionBookmark = (
  item: PracticeQuestion,
  questionId: string,
  isBookmarked: boolean
): PracticeQuestion => {
  if (isShortAnswerQuestionGroup(item)) {
    const hasSubQuestion = item.sub_questions.some((sub) => sub.id === questionId);
    if (!hasSubQuestion) return item;

    return {
      ...item,
      sub_questions: item.sub_questions.map((sub) =>
        sub.id === questionId ? { ...sub, is_bookmarked: isBookmarked } : sub
      ),
    };
  }

  if (item.id === questionId) {
    return { ...item, is_bookmarked: isBookmarked };
  }

  return item;
};

const TagPractice: React.FC = () => {
  const defaultAreas = SUBJECT_AREAS[useActiveSubjectId()];
  const { refreshUser } = useAuth();
  const [tagDirectory, setTagDirectory] = useState<TagDirectory>({});
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [isDirectoryLoading, setIsDirectoryLoading] = useState(true);

  const [selectedTags, setSelectedTags] = useState<TagDirectoryEntry[]>([]);
  const selectedTagIds = useMemo(() => selectedTags.map((tag) => tag.id), [selectedTags]);
  const [excludedTags, setExcludedTags] = useState<TagDirectoryEntry[]>([]);
  const [excludedGroups, setExcludedGroups] = useState<string[]>([]);

  const [questionQueue, setQuestionQueue] = useState<PracticeQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1);
  const [question, setQuestion] = useState<PracticeQuestion | null>(null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [attemptResult, setAttemptResult] = useState<AttemptResult | null>(null);
  const [answerStartTime, setAnswerStartTime] = useState<number>(Date.now());
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [questionTypeSelection, setQuestionTypeSelection] = useState<QuestionTypeSelection>('BOTH');
  const [tagCounts, setTagCounts] = useState<Record<string, number>>({});
  const [selectedQuestionCount, setSelectedQuestionCount] = useState<number | null>(null);
  const [isTagCountsLoading, setIsTagCountsLoading] = useState(false);

  const {
    subscriptionStatus,
    isLoading: isSubscriptionLoading,
    refresh: refreshSubscriptionStatus,
    setSubscriptionStatus,
  } = useSubscriptionStatus();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Practice by tags';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const practiceUsage = subscriptionStatus?.free_tier_usage;
  const practiceLimitReached = useMemo(() => {
    if (!subscriptionStatus?.is_on_free_plan || !practiceUsage) {
      return false;
    }
    return practiceUsage.practice_questions_used >= practiceUsage.practice_questions_limit;
  }, [practiceUsage, subscriptionStatus?.is_on_free_plan]);

  const practiceUsageLabel = useMemo(() => {
    if (!practiceUsage) {
      return null;
    }
    return `${practiceUsage.practice_questions_used}/${practiceUsage.practice_questions_limit} questions`;
  }, [practiceUsage]);

  const practiceResetLabel = useMemo(() => {
    if (!practiceUsage) return null;
    try {
      const resetDate = new Date(practiceUsage.resets_at);
      return `Resets ${resetDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}`;
    } catch {
      return `Resets ${practiceUsage.resets_on}`;
    }
  }, [practiceUsage]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        setIsDirectoryLoading(true);
        setDirectoryError(null);
        const directory: TagDirectoryResponse = await questionAPI.getTagDirectory();
        const populated: TagDirectory = {};

        Object.entries(directory).forEach(([area, tags]) => {
          populated[area] = tags.map((tag) => ({
            ...tag,
            area_of_study: tag.area_of_study ?? area,
          }));
        });

        defaultAreas.forEach((area) => {
          if (!populated[area]) {
            populated[area] = [];
          }
        });

        setTagDirectory(populated);
      } catch (error) {
        console.error('Failed to load tag directory', error);
        setDirectoryError('Unable to load tags. Please try again later.');
      } finally {
        setIsDirectoryLoading(false);
      }
    };

    loadTags();
  }, [defaultAreas]);

  const exclusionParams = useMemo(() => {
    const excludeTagIds = new Set(excludedTags.map((tag) => tag.id));
    const excludeAreas = new Set<string>();

    excludedGroups.forEach((groupName) => {
      if (groupName === 'Bookmarked') {
        excludeTagIds.add(BOOKMARKED_TAG_ID);
        return;
      }
      if (defaultAreas.includes(groupName)) {
        excludeAreas.add(groupName);
        return;
      }
      (tagDirectory[groupName] ?? []).forEach((tag) => excludeTagIds.add(tag.id));
    });

    return {
      exclude_tags: Array.from(excludeTagIds),
      exclude_areas: Array.from(excludeAreas),
    };
  }, [defaultAreas, excludedGroups, excludedTags, tagDirectory]);

  useEffect(() => {
    let isActive = true;

    const loadTagCounts = async () => {
      try {
        setIsTagCountsLoading(true);
        const response = await questionAPI.getTagFilterCounts({
          question_type: questionTypeSelection === 'BOTH' ? undefined : questionTypeSelection,
          tags: selectedTagIds,
          exclude_tags: exclusionParams.exclude_tags,
          exclude_areas: exclusionParams.exclude_areas,
        });

        if (!isActive) return;
        setTagCounts(response.counts_by_tag_id);
        setSelectedQuestionCount(response.selected_count);
      } catch (error) {
        console.error('Failed to load tag counts', error);
        if (!isActive) return;
        setTagCounts({});
        setSelectedQuestionCount(null);
      } finally {
        if (isActive) {
          setIsTagCountsLoading(false);
        }
      }
    };

    loadTagCounts();

    return () => {
      isActive = false;
    };
  }, [questionTypeSelection, selectedTagIds, exclusionParams]);

  const resetCurrentQuestion = useCallback(() => {
    setQuestion(null);
    setAttemptResult(null);
    setQuestionError(null);
  }, []);

  const resetPracticeSession = useCallback(() => {
    resetCurrentQuestion();
    setQuestionQueue([]);
    setCurrentQuestionIndex(-1);
  }, [resetCurrentQuestion]);

  const toggleTagSelection = (tag: TagDirectoryEntry) => {
    setSelectedTags((prev) => {
      const exists = prev.some((item) => item.id === tag.id);
      const updated = exists
        ? prev.filter((item) => item.id !== tag.id)
        : [...prev, tag];
      return updated;
    });
    setExcludedTags((prev) => prev.filter((item) => item.id !== tag.id));
    setExcludedGroups((prev) => prev.filter((groupName) => groupName !== tag.area_of_study));
    resetPracticeSession();
    setTagSearchTerm('');
  };

  const toggleTagExclusion = (tag: TagDirectoryEntry) => {
    setExcludedTags((prev) => {
      const exists = prev.some((item) => item.id === tag.id);
      return exists ? prev.filter((item) => item.id !== tag.id) : [...prev, tag];
    });
    setSelectedTags((prev) => prev.filter((item) => item.id !== tag.id));
    resetPracticeSession();
    setTagSearchTerm('');
  };

  const toggleGroupExclusion = (groupName: string) => {
    const tagsInGroup = tagDirectory[groupName] ?? [];
    const tagIdsInGroup = new Set(tagsInGroup.map((tag) => tag.id));
    setExcludedGroups((prev) => {
      const exists = prev.includes(groupName);
      return exists ? prev.filter((item) => item !== groupName) : [...prev, groupName];
    });
    setExcludedTags((prev) => prev.filter((tag) => !tagIdsInGroup.has(tag.id)));
    resetPracticeSession();
    setTagSearchTerm('');
  };

  const removeSelectedTag = (tagId: string) => {
    setSelectedTags((prev) => prev.filter((tag) => tag.id !== tagId));
    resetPracticeSession();
    setTagSearchTerm('');
  };

  const removeExcludedTag = (tagId: string) => {
    setExcludedTags((prev) => prev.filter((tag) => tag.id !== tagId));
    resetPracticeSession();
    setTagSearchTerm('');
  };

  const removeExcludedGroup = (groupName: string) => {
    setExcludedGroups((prev) => prev.filter((group) => group !== groupName));
    resetPracticeSession();
    setTagSearchTerm('');
  };

  const flattenedTags = useMemo(() => {
    const tagsById = new Map<string, TagDirectoryEntry>();
    Object.values(tagDirectory).flat().forEach((tag) => {
      if (!tagsById.has(tag.id)) {
        tagsById.set(tag.id, tag);
      }
    });
    return Array.from(tagsById.values());
  }, [tagDirectory]);

  const availableTagsForSearch = useMemo(() => {
    const selectedIds = new Set(selectedTags.map((tag) => tag.id));
    const excludedIds = new Set(excludedTags.map((tag) => tag.id));
    return flattenedTags.filter((tag) => !selectedIds.has(tag.id) && !excludedIds.has(tag.id));
  }, [excludedTags, flattenedTags, selectedTags]);

  const getProspectiveTagCount = useCallback(
    (tag: TagDirectoryEntry) => tagCounts[tag.id] ?? (selectedQuestionCount === null ? tag.question_count : 0),
    [selectedQuestionCount, tagCounts]
  );

  const filteredSearchResults = useMemo(() => {
    const term = tagSearchTerm.trim().toLowerCase();
    if (!term) {
      return [];
    }
    return availableTagsForSearch
      .filter((tag) => tag.name.toLowerCase().includes(term))
      .sort((a, b) => getProspectiveTagCount(b) - getProspectiveTagCount(a) || a.name.localeCompare(b.name))
      .slice(0, 10);
  }, [availableTagsForSearch, getProspectiveTagCount, tagSearchTerm]);

  const hasMatchingSelection = selectedQuestionCount === null || selectedQuestionCount > 0;
  const canStartSession = selectedTagIds.length > 0 && !loadingQuestion && !isTagCountsLoading && hasMatchingSelection;

  const handleAddTagFromSearch = (tag: TagDirectoryEntry) => {
    setSelectedTags((prev) => (prev.some((item) => item.id === tag.id) ? prev : [...prev, tag]));
    setExcludedTags((prev) => prev.filter((item) => item.id !== tag.id));
    setExcludedGroups((prev) => prev.filter((groupName) => groupName !== tag.area_of_study));
    setTagSearchTerm('');
    resetPracticeSession();
  };

  const submitAnswer = useCallback(
    async ({
      questionId,
      selectedAnswer,
      timeSpentSeconds,
    }: {
      questionId: string;
      selectedAnswer: string;
      timeSpentSeconds?: number;
    }) => {
      if (practiceLimitReached) {
        setShowLimitModal(true);
        throw new Error('Practice limit reached');
      }

      try {
        const result = await questionAPI.submitAnswer({
          question_id: questionId,
          selected_answer: selectedAnswer,
          time_spent_seconds: timeSpentSeconds,
          game_mode: 'practice',
        });

        if (result.free_tier_usage) {
          setSubscriptionStatus((prev) =>
            prev
              ? {
                  ...prev,
                  free_tier_usage: result.free_tier_usage,
                }
              : prev
          );
        }

        if (result.question_progress) {
          setQuestion((prev) => {
            if (!prev) {
              return prev;
            }

            if (isShortAnswerQuestionGroup(prev)) {
              return {
                ...prev,
                progress: result.question_progress ?? prev.progress,
                sub_questions: prev.sub_questions.map((sub) =>
                  sub.id === questionId
                    ? {
                        ...sub,
                        progress: result.question_progress ?? sub.progress,
                      }
                    : sub
                ),
              };
            }

            if (prev.id === questionId) {
              return {
                ...prev,
                progress: result.question_progress ?? prev.progress,
              };
            }

            return prev;
          });

          setQuestionQueue((prev) =>
            prev.map((item) => {
              if (isShortAnswerQuestionGroup(item)) {
                if (item.sub_questions.some((sub) => sub.id === questionId)) {
                  return {
                    ...item,
                    progress: result.question_progress ?? item.progress,
                    sub_questions: item.sub_questions.map((sub) =>
                      sub.id === questionId
                        ? {
                            ...sub,
                            progress: result.question_progress ?? sub.progress,
                          }
                        : sub
                    ),
                  };
                }
                return item;
              }

              if (item.id === questionId) {
                return {
                  ...item,
                  progress: result.question_progress ?? item.progress,
                };
              }

              return item;
            })
          );
        }

        await refreshUser();
        await refreshSubscriptionStatus().catch(() => {});
        return result;
      } catch (error: any) {
        if (isFreeTierLimitError(error)) {
          setShowLimitModal(true);
          setQuestion(null);
          await refreshSubscriptionStatus().catch(() => {});
          throw new Error('Daily practice limit reached.');
        }

        const message = getUserMessage(error, 'Failed to submit answer. Please try again.');
        throw new Error(message);
      }
    },
    [practiceLimitReached, refreshSubscriptionStatus, refreshUser, setSubscriptionStatus]
  );

  const startPracticeSession = useCallback(async () => {
    if (practiceLimitReached) {
      setShowLimitModal(true);
      resetPracticeSession();
      return;
    }

    if (selectedTagIds.length === 0) {
      setQuestionError('Select at least one tag to start practicing.');
      return;
    }

    if (selectedQuestionCount !== null && selectedQuestionCount <= 0) {
      setQuestionError('No questions match the selected tags. Try removing a tag or changing question type.');
      return;
    }

    try {
      setLoadingQuestion(true);
      setQuestionError(null);
      resetPracticeSession();

      const params: Record<string, any> = {
        tags: selectedTagIds,
        exclude_tags: exclusionParams.exclude_tags,
        exclude_areas: exclusionParams.exclude_areas,
        limit: 100,
        skip: 0,
      };

      const typeParam = questionTypeSelection === 'BOTH' ? undefined : questionTypeSelection;
      if (typeParam) {
        params.question_type = typeParam;
      }

      const response = await questionAPI.getQuestions(params);
      const apiQuestions: QuestionApiModel[] = Array.isArray(response) ? response : [];

      if (!apiQuestions || apiQuestions.length === 0) {
        setQuestionError('No questions match the selected tags. Try choosing different tags.');
        return;
      }

      const sortedQuestions = buildPracticeQuestionSet(apiQuestions, questionTypeSelection);

      if (sortedQuestions.length === 0) {
        setQuestionError('No questions match the selected tags. Try choosing different tags.');
        return;
      }

      setQuestionQueue(sortedQuestions);
      setCurrentQuestionIndex(0);
      setQuestion(sortedQuestions[0]);
      setAttemptResult(null);
      setAnswerStartTime(Date.now());
      await refreshSubscriptionStatus().catch(() => {});
    } catch (error: any) {
      console.error('Failed to load questions by tags', error);
      if (isFreeTierLimitError(error)) {
        setShowLimitModal(true);
        setQuestion(null);
        await refreshSubscriptionStatus().catch(() => {});
      } else if (error?.response?.status === 404) {
        setQuestionError('No questions match the selected tags. Try choosing different tags.');
      } else {
        setQuestionError('Unable to load questions. Please try again.');
      }
    } finally {
      setLoadingQuestion(false);
    }
  }, [exclusionParams, practiceLimitReached, questionTypeSelection, refreshSubscriptionStatus, resetPracticeSession, selectedQuestionCount, selectedTagIds]);

  const handleAnswer = useCallback(async (selectedAnswerValue: string, timeSpentSeconds?: number) => {
    if (!question || submittingAnswer) return;
    if (isShortAnswerQuestionGroup(question)) return;
    if (practiceLimitReached) {
      setShowLimitModal(true);
      return;
    }

    setSubmittingAnswer(true);
    try {
      const resolvedTimeSpentSeconds =
        typeof timeSpentSeconds === 'number'
          ? timeSpentSeconds
          : Math.floor((Date.now() - answerStartTime) / 1000);

      const result = await submitAnswer({
        questionId: question.id,
        selectedAnswer: selectedAnswerValue,
        timeSpentSeconds: resolvedTimeSpentSeconds,
      });

      setAttemptResult(result);
    } catch (error) {
      console.error('Failed to submit answer', error);
      setQuestionError(getUserMessage(error, 'Failed to submit answer. Please try again.'));
    } finally {
      setSubmittingAnswer(false);
    }
  }, [answerStartTime, practiceLimitReached, question, submitAnswer, submittingAnswer]);

  const handleShortAnswerSubmission = useCallback(
    async ({
      questionId,
      selectedAnswer,
      timeSpentSeconds,
    }: {
      questionId: string;
      selectedAnswer: string;
      timeSpentSeconds?: number;
    }) => {
      return submitAnswer({
        questionId,
        selectedAnswer,
        timeSpentSeconds,
      });
    },
    [submitAnswer]
  );


  const handleBookmarkChange = useCallback((questionId: string, isBookmarked: boolean) => {
    setQuestion((prev) => (prev ? updatePracticeQuestionBookmark(prev, questionId, isBookmarked) : prev));
    setQuestionQueue((prev) =>
      prev.map((item) => updatePracticeQuestionBookmark(item, questionId, isBookmarked))
    );

    const delta = isBookmarked ? 1 : -1;
    setTagDirectory((prev) => {
      const bookmarkedTags = prev.Bookmarked;
      if (!bookmarkedTags) return prev;
      return {
        ...prev,
        Bookmarked: bookmarkedTags.map((tag) =>
          tag.id === BOOKMARKED_TAG_ID
            ? { ...tag, question_count: Math.max(0, tag.question_count + delta) }
            : tag
        ),
      };
    });
    setTagCounts((prev) => {
      if (!(BOOKMARKED_TAG_ID in prev)) return prev;
      return {
        ...prev,
        [BOOKMARKED_TAG_ID]: Math.max(0, prev[BOOKMARKED_TAG_ID] + delta),
      };
    });
    if (selectedTagIds.includes(BOOKMARKED_TAG_ID)) {
      setSelectedQuestionCount((prev) => (prev === null ? prev : Math.max(0, prev + delta)));
    }
  }, [selectedTagIds]);

  const handleQuestionTypeFilterChange = useCallback(
    (selection: QuestionTypeSelection) => {
      setQuestionTypeSelection(selection);
      resetPracticeSession();
    },
    [resetPracticeSession]
  );

  const goToQuestionIndex = useCallback((nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= questionQueue.length) {
      return;
    }

    setCurrentQuestionIndex(nextIndex);
    setQuestion(questionQueue[nextIndex]);
    setAttemptResult(null);
    setQuestionError(null);
    setAnswerStartTime(Date.now());
  }, [questionQueue]);

  const handlePreviousQuestion = useCallback(() => {
    goToQuestionIndex(currentQuestionIndex - 1);
  }, [currentQuestionIndex, goToQuestionIndex]);

  const handleNextQuestion = useCallback(() => {
    const nextIndex = currentQuestionIndex + 1;

    if (nextIndex >= questionQueue.length) {
      setQuestion(null);
      setCurrentQuestionIndex(-1);
      setQuestionError('You have completed all questions for these tags. Adjust your selection to continue.');
      setAttemptResult(null);
      return;
    }

    goToQuestionIndex(nextIndex);
  }, [currentQuestionIndex, goToQuestionIndex, questionQueue.length]);

  const hasPreviousQuestion = currentQuestionIndex > 0;
  const hasNextQuestion = currentQuestionIndex >= 0 && currentQuestionIndex < questionQueue.length - 1;
  const questionSetProgressLabel = currentQuestionIndex >= 0 && questionQueue.length > 0
    ? `Question ${currentQuestionIndex + 1} of ${questionQueue.length}`
    : null;

  const isLoadingState = isDirectoryLoading || (isSubscriptionLoading && !question);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Practice by tags</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose tags to build a targeted practice session.
              </p>
            </div>
            <Link
              to="/dashboard"
              className="text-sm text-subject-600 hover:text-subject-500 dark:text-subject-400"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {practiceUsage && (
          <div className="mb-6">
            <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily Practice Limit</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {practiceUsageLabel}
                </p>
              </div>
              {practiceResetLabel && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{practiceResetLabel}</span>
              )}
            </div>
          </div>
        )}

        {isLoadingState ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-subject-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
            {/* Tag directory */}
            <aside className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 h-fit">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tag Directory</h2>

              {directoryError ? (
                <div className="text-sm text-red-600 dark:text-red-400">
                  {directoryError}
                </div>
              ) : (
                <div className="space-y-5">
                  {defaultAreas.concat(Object.keys(tagDirectory).filter((area) => !defaultAreas.includes(area))).map((area) => {
                    const tags = tagDirectory[area] ?? [];
                    if (tags.length === 0) {
                      return (
                        <div key={area}>
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                            {area}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">No tags available yet.</p>
                        </div>
                      );
                    }

                    return (
                      <div key={area}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                            {area}
                          </h3>
                          <button
                            type="button"
                            onClick={() => toggleGroupExclusion(area)}
                            className={`px-2 py-1 rounded-full border text-[10px] font-semibold transition-colors ${
                              excludedGroups.includes(area)
                                ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700'
                                : 'bg-white text-gray-500 border-gray-200 hover:border-red-300 hover:text-red-600 dark:bg-gray-900 dark:text-gray-400 dark:border-gray-700'
                            }`}
                          >
                            {excludedGroups.includes(area) ? 'Excluded' : 'Exclude group'}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {tags.map((tag) => {
                            const isSelected = selectedTagIds.includes(tag.id);
                            const isExcluded = excludedTags.some((item) => item.id === tag.id) || excludedGroups.includes(area);
                            const displayCount = isSelected
                              ? selectedQuestionCount ?? tag.question_count
                              : getProspectiveTagCount(tag);
                            const isDisabled = !isSelected && !isExcluded && selectedQuestionCount !== null && displayCount === 0;
                            return (
                              <span
                                key={tag.id}
                                data-testid={`available-tag-chip-${tag.id}`}
                                className="inline-flex max-w-full flex-wrap rounded-full border border-gray-200 dark:border-gray-700 overflow-hidden"
                              >
                                <button
                                  disabled={isDisabled || isExcluded}
                                  onClick={() => toggleTagSelection(tag)}
                                  className={`min-w-0 max-w-full px-3 py-1 text-left text-xs font-medium whitespace-normal break-words transition-colors ${
                                    isSelected
                                      ? 'bg-subject-600 text-white'
                                      : isExcluded
                                        ? 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-300 cursor-not-allowed'
                                        : isDisabled
                                          ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-subject-50 dark:hover:bg-subject-900/30'
                                  }`}
                                >
                                  <span data-testid={`available-tag-name-${tag.id}`} className="break-words whitespace-normal">
                                    {tag.name}
                                  </span>
                                  <span className="ml-2 text-[10px] font-semibold opacity-80">
                                    {isTagCountsLoading ? '...' : displayCount}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleTagExclusion(tag)}
                                  className={`px-2 py-1 text-[10px] font-semibold transition-colors border-l border-gray-200 dark:border-gray-700 ${
                                    isExcluded
                                      ? 'bg-red-600 text-white'
                                      : 'bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20'
                                  }`}
                                  aria-label={`${isExcluded ? 'Stop excluding' : 'Exclude'} ${tag.name}`}
                                >
                                  {isExcluded ? '−' : 'Exclude'}
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </aside>

            {/* Practice panel */}
            <main className="space-y-6">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Selected Tags</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedTags.length === 0
                        ? 'Choose at least one tag to enable practice.'
                        : isTagCountsLoading
                          ? 'Checking matching questions...'
                          : `${selectedQuestionCount ?? 0} matching ${(selectedQuestionCount ?? 0) === 1 ? 'question' : 'questions'} for this combination.`}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden">
                      {(
                        [
                          { label: 'All Types', value: 'BOTH' },
                          { label: 'Multiple Choice', value: 'MC' },
                          { label: 'Short Answer', value: 'SA' },
                          { label: 'Extended Response', value: 'ER' },
                        ] as { label: string; value: QuestionTypeSelection }[]
                      ).map((option) => {
                        const isActive = questionTypeSelection === option.value;
                        return (
                          <button
                            key={option.value}
                            onClick={() => handleQuestionTypeFilterChange(option.value)}
                            className={`px-3 py-2 text-xs font-medium transition-colors ${
                              isActive
                                ? 'bg-subject-600 text-white'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-subject-50 dark:hover:bg-subject-900/30'
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={startPracticeSession}
                      disabled={!canStartSession}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        !canStartSession
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-subject-600 text-white hover:bg-subject-500'
                      }`}
                    >
                      {loadingQuestion ? 'Loading...' : 'Start Session'}
                    </button>
                  </div>
                </div>

                {selectedTags.length === 0 ? (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    No tags selected yet. Click a tag from the directory or search to add it here.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="selected-tags-summary">
                    {selectedTags.map((tag) => (
                      <span
                        key={tag.id}
                        data-testid={`selected-tag-chip-${tag.id}`}
                        className="inline-flex max-w-full items-center px-3 py-1 rounded-full bg-subject-100 text-subject-800 dark:bg-subject-900/30 dark:text-subject-200 text-xs font-medium whitespace-normal break-words"
                      >
                        <span className="min-w-0 break-words whitespace-normal">{tag.name}</span>
                        <button
                          onClick={() => removeSelectedTag(tag.id)}
                          className="ml-2 text-subject-700 hover:text-subject-500 dark:text-subject-300"
                          aria-label={`Remove ${tag.name}`}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {excludedGroups.length > 0 || excludedTags.length > 0 ? (
                  <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Excluded</h3>
                    <div className="flex flex-wrap gap-2" data-testid="excluded-tags-summary">
                      {excludedGroups.map((groupName) => (
                        <span
                          key={`group-${groupName}`}
                          className="inline-flex max-w-full items-center px-3 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 text-xs font-medium whitespace-normal break-words"
                        >
                          <span className="min-w-0 break-words whitespace-normal">{groupName} group</span>
                          <button
                            onClick={() => removeExcludedGroup(groupName)}
                            className="ml-2 text-red-700 hover:text-red-500 dark:text-red-300"
                            aria-label={`Stop excluding ${groupName}`}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </span>
                      ))}
                      {excludedTags.map((tag) => (
                        <span
                          key={`tag-${tag.id}`}
                          data-testid={`excluded-tag-chip-${tag.id}`}
                          className="inline-flex max-w-full items-center px-3 py-1 rounded-full bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-200 text-xs font-medium whitespace-normal break-words"
                        >
                          <span className="min-w-0 break-words whitespace-normal">{tag.name}</span>
                          <button
                            onClick={() => removeExcludedTag(tag.id)}
                            className="ml-2 text-red-700 hover:text-red-500 dark:text-red-300"
                            aria-label={`Stop excluding ${tag.name}`}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/40 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <label htmlFor="tag-search" className="block text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-2">
                  Search Tags
                </label>
                <input
                  id="tag-search"
                  type="text"
                  value={tagSearchTerm}
                  onChange={(event) => setTagSearchTerm(event.target.value)}
                  placeholder="Start typing to search..."
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-subject-500 focus:ring-subject-500"
                />
                {filteredSearchResults.length > 0 && (
                  <div
                    data-testid="tag-search-results"
                    className="mt-3 border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden"
                  >
                    {filteredSearchResults.map((tag) => {
                      const count = getProspectiveTagCount(tag);
                      const isDisabled = selectedQuestionCount !== null && count === 0;
                      return (
                        <button
                          key={tag.id}
                          disabled={isDisabled}
                          onClick={() => handleAddTagFromSearch(tag)}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                            isDisabled
                              ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              : 'bg-white dark:bg-gray-800 hover:bg-subject-50 dark:hover:bg-subject-900/30'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="min-w-0 break-words whitespace-normal font-medium text-gray-900 dark:text-white">{tag.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {isTagCountsLoading ? '...' : `${count} if added`}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{tag.area_of_study}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {questionError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                  {questionError}
                </div>
              )}

              {question ? (
                <>
                  {questionSetProgressLabel && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{questionSetProgressLabel}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Flick through the selected set without submitting first.</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handlePreviousQuestion}
                          disabled={!hasPreviousQuestion}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            hasPreviousQuestion
                              ? 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed border border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={handleNextQuestion}
                          disabled={!hasNextQuestion}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            hasNextQuestion
                              ? 'bg-subject-600 text-white hover:bg-subject-500'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                  {!isShortAnswerQuestionGroup(question) ? (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    onAnswer={handleAnswer}
                    isSubmitting={submittingAnswer}
                    attemptResult={attemptResult}
                    workedSolution={question.worked_solution ?? undefined}
                    showSimilarQuestion
                    canGenerateSimilar={!!subscriptionStatus?.has_active_subscription}
                    showTimer
                    onBookmarkChange={handleBookmarkChange}
                  />
                ) : (
                  <ShortAnswerQuestionCard
                    key={question.id}
                    question={question}
                    onSubmitAnswer={({ questionId, selectedAnswer, timeSpentSeconds }) =>
                      handleShortAnswerSubmission({ questionId, selectedAnswer, timeSpentSeconds })
                    }
                    practiceLimitReached={practiceLimitReached}
                    showSimilarQuestion
                    canGenerateSimilar={!!subscriptionStatus?.has_active_subscription}
                    showTimer
                    onBookmarkChange={handleBookmarkChange}
                  />
                )}
                </>
              ) : (
                <div className="bg-white dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-10 text-center text-gray-600 dark:text-gray-400">
                  <p className="text-base font-medium mb-2">Select tags to begin practicing.</p>
                  <p className="text-sm">Use the directory to build a custom question set.</p>
                </div>
              )}
            </main>
          </div>
        )}
      </div>

      <FreeTierLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        feature="practice"
        usage={subscriptionStatus?.free_tier_usage}
      />
    </div>
  );
};

export default TagPractice;
